import {
  ChangeDetectorRef,
  ComponentRef,
  ElementRef,
  Inject,
  Injectable,
  Injector,
  NgZone,
  OnDestroy,
  Optional,
  SkipSelf,
  Type,
  ViewContainerRef,
  ɵdetectChanges as detectChanges,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, EMPTY, merge, Observable, Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';

import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../core/overlay/overlay-directives';
import { OverlayService } from '../core/overlay/overlay.service';
import { ScrollDispatcher } from '../core/overlay/scroll/scroll-dispatcher';
import { ComponentPortal } from '../core/portal/portal';
import { DirDirective } from '../core/rtl/rtl.module';
import { ComponentTooltipResizeConsumer } from './resize-providers';
import {
  ArrowPosition,
  ComponentPositionPair,
  ComponentTooltipPosition,
  ComponentTooltipServiceConfig,
  ObserveOnElement,
} from './types';

@Injectable()
export class ComponentTooltipService<
  C extends Type<{
    ngOnDestroy$: Observable<void>;
    updateArrow: (arrowPosition: ArrowPosition) => void;
  }>,
  T,
> implements OnDestroy
{
  public connectedOverlayDirective: ConnectedOverlayDirective;
  public data$: BehaviorSubject<T | null> = new BehaviorSubject(null);

  private component: C;
  private componentData: T;
  private componentRef: ComponentRef<{
    ngOnDestroy$: Observable<void>;
    updateArrow: (arrowPosition: ArrowPosition) => void;
  }> | null = null;
  private config: ComponentTooltipServiceConfig;
  private destroyTooltip$ = new Subject<void>();
  private mutationObserver: MutationObserver;

  constructor(
    private elementRef: ElementRef,
    private overlay: OverlayService,
    private vcRef: ViewContainerRef,
    private _cdRef: ChangeDetectorRef,
    private injector: Injector,
    private _ngZone: NgZone,
    private router: Router,
    @SkipSelf() private _parentCdRef: ChangeDetectorRef,
    @Optional() private _scrollDispatcher: ScrollDispatcher,
    @Optional() private _dir: DirDirective,
    @Optional() @Inject('overlayLevel') private _level: number,
    @Optional() private componentTooltipResize?: ComponentTooltipResizeConsumer,
  ) {}

  public ngOnDestroy(): void {
    this.componentRef = null;
    this.closeTooltip();
    this.connectedOverlayDirective?.ngOnDestroy();
  }

  public hasConfig(): boolean {
    return !!this.config;
  }

  public setConfig(config: ComponentTooltipServiceConfig): void {
    this.config = config;
  }

  public setComponent(component: C): void {
    this.component = component;
  }

  public setComponentData(data: T): void {
    this.componentData = data;
    this.data$.next(data);
  }

  public openTooltip(originRef?: ElementRef): ComponentRef<InstanceType<C>> {
    if (!this.connectedOverlayDirective || originRef) {
      this.connectedOverlayDirective = this.setupConnectedOverlayDirective();
      this.watchPositionUpdateEvents();
    }

    const portal = new ComponentPortal(this.component, null, this.injector);
    this.connectedOverlayDirective.positions = this.getPositions();
    this.connectedOverlayDirective.origin = new OverlayOriginDirective(
      originRef || this.elementRef,
    );
    this.connectedOverlayDirective.portal = portal;
    this.componentRef = <ComponentRef<InstanceType<C>>>(
      this.connectedOverlayDirective.toggleOverlay(true)
    );
    this.connectedOverlayDirective.positionChange
      .pipe(take(1))
      .subscribe(() => this.updateTooltipPosition());
    this.startObserveOnElement();
    this.updateTooltipPosition();
    return <ComponentRef<InstanceType<C>>>this.componentRef;
  }

  public closeTooltip(): void {
    this.destroyTooltip$.next();

    if (this.connectedOverlayDirective) {
      this.connectedOverlayDirective.open = false;
    }
    this.stopObserveOnElement();
  }

  private setupConnectedOverlayDirective() {
    const parentCdRef = this.getParentCdRef();
    const connectedOverlayDirective = new ConnectedOverlayDirective(
      null,
      this.vcRef,
      this.overlay,
      this._cdRef,
      this._ngZone,
      parentCdRef,
      this._scrollDispatcher,
      this._dir,
      this._level,
    );
    connectedOverlayDirective.hasBackdrop = this.config.hasBackdrop;
    connectedOverlayDirective.backdropClass = this.config.backdropClass || '';
    connectedOverlayDirective.autoCloseOnUnpreventedClick = false;
    connectedOverlayDirective.position = Object.values(ComponentTooltipPosition);
    connectedOverlayDirective.overlayClipArea = this.config.overlayClipArea;

    return connectedOverlayDirective;
  }

  private getPositions() {
    return <ComponentPositionPair[]>Object.values(this.config.positions);
  }

  private updateTooltipPosition() {
    const availablePositions = this.getPositions();
    const { positionStrategy } = this.connectedOverlayDirective;
    if (!positionStrategy) return;

    const position = positionStrategy.findPosition(
      this.getOverlayElement(),
      availablePositions,
      this.config.withFallbackPoint,
    );

    if (position) {
      this.lockTooltipPosition(position);
      this.connectedOverlayDirective.offsetX = position.offsetX;
      this.connectedOverlayDirective.offsetY = position.offsetY;
      this.connectedOverlayDirective.overlayRef.updatePosition();

      if (this.componentRef) {
        const { instance, changeDetectorRef } = this.componentRef;
        instance.updateArrow(position.arrowPosition);
        changeDetectorRef.detectChanges();
      }
    }
  }

  private watchPositionUpdateEvents(): void {
    merge<unknown[]>(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
      this.componentTooltipResize ?? EMPTY,
    )
      .pipe(debounceTime(5), takeUntil(this.destroyTooltip$))
      .subscribe(() => this.updateTooltipPosition());
  }

  private getOverlayElement(): HTMLElement {
    return this.connectedOverlayDirective.overlayRef.overlayElement;
  }

  private lockTooltipPosition(position: ComponentPositionPair) {
    const { positionStrategy } = this.connectedOverlayDirective;
    if (positionStrategy) {
      this.connectedOverlayDirective.positions = [position];
      positionStrategy._preferredPositions = [position];
    }
  }

  private getParentCdRef(): ChangeDetectorRef {
    // workaround angular bug
    const parentCdRef = this._parentCdRef;
    if ((<any>this._cdRef)._lView === (<any>this._parentCdRef)._lView) {
      parentCdRef.detectChanges = () => detectChanges(this.config.context);
    }
    return parentCdRef;
  }

  private startObserveOnElement(): void {
    if (!this.config.observeOnElement) return;

    this.mutationObserver = new MutationObserver(() => this.updateTooltipPosition());

    // eslint-disable-next-line default-case
    switch (this.config.observeOnElement) {
      case ObserveOnElement.Self: {
        this.mutationObserver.observe(this.elementRef.nativeElement, {
          attributes: true,
        });
      }
      case ObserveOnElement.Parent: {
        this.mutationObserver.observe(this.elementRef.nativeElement.parentElement, {
          attributes: true,
          subtree: true,
        });
      }
    }
  }

  private stopObserveOnElement(): void {
    if (this.config?.observeOnElement && this.mutationObserver) this.mutationObserver.disconnect();
  }
}
